import { ENV } from '../config/environment';
import { authenticatedFetch } from '../utils/authenticatedFetch';

/**
 * ⚔️ WINGMAN POI INTELLIGENCE ENGINE (PHASE 1)
 * 
 * Provides canonical TripAdvisor venues through Wingman's authenticated gateway.
 */

export interface SpatialVenue {
    id: string;
    name: string;
    rating?: number;
    price: string;
    image: string;
    lat: number;
    lng: number;
    source: 'tripadvisor';
}

interface TripadvisorPlacePayload {
    placeId?: unknown;
    name?: unknown;
    rating?: unknown;
    price?: unknown;
    image?: unknown;
    latitude?: unknown;
    longitude?: unknown;
}

function optionalFiniteNumber(value: unknown): number | undefined {
    if ((typeof value !== 'number' && typeof value !== 'string') || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

class PoiService {
    private static lastCallTime = 0;
    private static cache: Record<string, SpatialVenue[]> = {};

    private static async fetchFromGateway(query: string): Promise<TripadvisorPlacePayload[]> {
        const response = await authenticatedFetch(`${ENV.GATEWAY_URL}/api/search/tripadvisor/places`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: `restaurants in ${query}` }),
        });

        if (!response.ok) {
            throw new Error(`TripAdvisor gateway error: ${response.status} ${response.statusText}`);
        }

        const payload = await response.json();
        return Array.isArray(payload?.places) ? payload.places : [];
    }

    /**
     * Normalizes raw TripAdvisor data into the Wingman Spatial format.
     */
    private static mapToSpatialVenue(raw: TripadvisorPlacePayload): SpatialVenue | null {
        const id = typeof raw.placeId === 'string' ? raw.placeId.trim() : '';
        const name = typeof raw.name === 'string' ? raw.name.trim() : '';
        const lat = optionalFiniteNumber(raw.latitude);
        const lng = optionalFiniteNumber(raw.longitude);
        if (!id || !name || lat === undefined || lng === undefined) return null;

        const rating = optionalFiniteNumber(raw.rating);
        return {
            id,
            name,
            rating,
            price: typeof raw.price === 'string' ? raw.price : '',
            image: typeof raw.image === 'string' ? raw.image : '',
            lat,
            lng,
            source: 'tripadvisor'
        };
    }

    /**
     * MAIN ENTRY POINT: Searches canonical TripAdvisor places through the Wingman gateway.
     */
    public static async searchRestaurants(query: string): Promise<SpatialVenue[]> {
        // 🛡️ RATE LIMIT GUARD
        const now = Date.now();
        if (now - this.lastCallTime < 1000) {
            console.info("[PoiService] Throttled: Returning cached or empty result.");
            return this.cache[query] || [];
        }
        this.lastCallTime = now;

        try {
            const places = await this.fetchFromGateway(query);
            const restaurants = places
                .slice(0, 10)
                .map(place => this.mapToSpatialVenue(place))
                .filter((place): place is SpatialVenue => place !== null);

            if (restaurants.length > 0) this.cache[query] = restaurants;
            return restaurants;
        } catch (error) {
            console.warn(`[PoiService] TripAdvisor gateway search failed for "${query}":`, error);
            return [];
        }
    }
}

export { PoiService };
